import React, { useContext } from 'react';
import { Box, Flex } from 'rebass';
import {
  Label,
  Radio as RebassRadio,
  RadioProps as RebassRadioProps,
} from '@rebass/forms';
import camelcase from 'lodash.camelcase';
import { FocusContext } from '../../../contexts/FocusContext';
import { RadioGroupFieldContext } from './RadioGroupField';

export interface RadioProps extends RebassRadioProps {
  label: string;
  value: string | number;
  icon?: React.FC;
  variant?: string;
}

export const Radio: React.FC<RadioProps> = ({
  label,
  value,
  icon,
  variant = 'classicRadio',
  children,
}) => {
  const { setFocus } = useContext(FocusContext);
  const { selectedValue, setSelectedValue, name } = useContext(
    RadioGroupFieldContext
  );
  const id = camelcase(`${name} is ${value}`);

  const isSelected = value === selectedValue;

  const labelVariant = selectedValue
    ? isSelected
      ? `${variant}.selectedLabel`
      : `${variant}.unselectedLabel`
    : `${variant}.label`;

  const inputProps = {
    id,
    'data-testid': id,
    onChange: () => {
      setSelectedValue(value);
    },
    value,
    name,
    onFocus: () => {
      setFocus(true);
    },
    onBlur: () => {
      setFocus(false);
    },
    checked: isSelected,
    'aria-checked': isSelected,
  };

  return (
    <Box>
      <Flex tx="forms">
        <Label
          htmlFor={id}
          data-testid={`label-${inputProps['data-testid']}`}
          variant={labelVariant}
          onMouseDown={e => e.preventDefault()}
        >
          {variant === 'radioPill' ? (
            <>
              <Box as="input" type="radio" role="radio" {...inputProps} />
              {icon && <Box as={icon} />}
            </>
          ) : (
            <RebassRadio variant={variant} {...inputProps} />
          )}
          {label}
        </Label>
      </Flex>
      {isSelected && <>{children}</>}
    </Box>
  );
};
